"use client";

import { useDataFastGoals } from "@/hooks/useDataFastGoals";
import { useEngagedView } from "@/hooks/useEngagedView";
import { RatingBadge } from "./reviews/rating-badge";
import { useEffect } from "react";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { HeroVideo } from "./hero-video";
import { buttonClass, CONTAINER, scrollToSection } from "./ui-tokens";
import leonardoBastos from "@/assets/igrejas/pessoas/pr-leonardo-bastos-ministerio-decisao.webp";
import rodrigoBortolazo from "@/assets/igrejas/pessoas/pr-rodrigo-bortolazo-ieq-taquaral.webp";
import ruanMagalhaes from "@/assets/igrejas/pessoas/pr-ruan-magalhaes-comunidade-crista-zelo.webp";
import rogerioZaiyt from "@/assets/igrejas/pessoas/pr-rogerio-zaiyt-maip.webp";
import carinaDetoni from "@/assets/igrejas/pessoas/pr-carina-detoni-ieq-central.webp";

// Cada rosto é líder de uma igreja da vitrine do `trusted-by` — o nome do
// arquivo carrega a igreja justamente para que a fileira continue auditável
// quando alguém quiser saber de onde veio a foto. Rosto sem igreja na vitrine
// não entra: a fileira é prova social, não galeria.
const proofFaces = [
  { src: rodrigoBortolazo, name: "Pr. Rodrigo Bortolazo" },
  { src: ruanMagalhaes, name: "Pr. Ruan Magalhães" },
  { src: carinaDetoni, name: "Pra. Carina Detoni" },
  { src: rogerioZaiyt, name: "Pr. Rogério Zaiyt" },
  { src: leonardoBastos, name: "Pr. Leonardo Bastos" },
];

// Um ganho por quem trabalha na igreja. O H1 fala com quem decide; esta faixa
// é onde cada pessoa que vai usar o sistema se encontra pelo nome, e é o que
// a secretaria mostra ao pastor (e o pastor ao conselho) para dizer "isso é
// para nós". Áreas diferentes de propósito: a especificidade chega sem
// estreitar o produto a um módulo (ver o comentário do H1).
//
// Cada ganho é a promessa que a página sustenta mais abaixo: o esfriamento e
// o cadastro no pós-culto em features.tsx e comparison.tsx, os 15 minutos no
// bloco de escalas, o fechamento no bloco de finanças.
const proofPoints = [
  { who: "Pastor", what: "vê quem está esfriando antes de a pessoa sumir" },
  { who: "Secretaria", what: "cadastra o visitante no pós-culto, sem ficha de papel" },
  { who: "Líderes", what: "publicam a escala do mês em 15 minutos" },
  { who: "Tesouraria", what: "fecha o mês sem planilha" },
];

export default function Hero() {
  const { trackLandingView, trackHeroCtaClicked, trackHeroLearnMoreClicked } =
    useDataFastGoals();

  useEffect(() => {
    trackLandingView();
  }, [trackLandingView]);

  // `landing_view` acima conta carregamentos; este conta visitas com gente.
  useEngagedView("landing");

  return (
    <section
      id="inicio"
      aria-labelledby="hero-titulo"
      className="relative overflow-hidden pt-14 md:pt-20"
    >
      <div className={CONTAINER}>
        <div className="max-w-3xl">
          {/* Duas versões anteriores do H1 e o que cada uma errava, porque a
              tentação de reescrever este título volta sempre:

              1. "Mais tempo pastoreando. Menos tempo com planilhas e
                 WhatsApp." Verdade, mas é a promessa que QUALQUER sistema de
                 igreja assina sem trocar uma palavra.
              2. "Sábado, 22h: 'pastor, não vou poder servir amanhã.'"
                 Específica, mas estreita o produto a um módulo entre vinte no
                 momento em que o visitante forma o modelo do que isto é — e
                 filtra pelo eixo errado ("sofro com escala" em vez de "é para
                 a minha igreja").

              A regra que sobrou: dizer O QUE É e PARA QUEM em cinco segundos,
              e tirar a especificidade do escopo e da velocidade. A prova
              módulo a módulo fica com a lista de três itens logo abaixo.

              Sem animação de entrada no H1: ele é o LCP da página. */}
          <h1
            id="hero-titulo"
            className="text-4xl leading-[1.04] font-semibold tracking-tight text-balance text-foreground sm:text-5xl lg:text-[4rem]"
          >
            A igreja inteira num sistema só.{" "}
            <span className="text-muted-foreground">
              A migração é por nossa conta.
            </span>
          </h1>

          <p
            className="lp-enter mt-6 max-w-2xl text-lg leading-relaxed text-pretty text-muted-foreground"
            style={{ "--lp-i": 1 } as React.CSSProperties}
          >
            {/* A lista de módulos sozinha era inventário: todo concorrente
                assina igual. "Mostra quem começa a sumir" é o
                mecanismo do bloco de Pessoas, e o que diferencia o produto
                na primeira tela. */}
            Membros, visitantes, células, kids, escalas e financeiro no mesmo
            lugar, num sistema que mostra quem começa a sumir. Os 20
            módulos vêm liberados em qualquer plano, e a nossa equipe traz a
            sua planilha para a igreja operar já no próximo culto.
          </p>

          {/* O id é o gatilho da barra fixa do mobile — ver use-sticky-cta.ts.
              Se renomear ou remover, a barra volta a observar o hero inteiro e
              demora ~2,5 telas a mais para aparecer. */}
          <div
            id="hero-cta"
            className="lp-enter mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
            style={{ "--lp-i": 2 } as React.CSSProperties}
          >
            {/* O botão promete começar o teste, então ele começa o teste — é
                o mesmo destino do CTA do cabeçalho. O preço segue a um clique,
                no botão ao lado e na barra fixa do mobile. */}
            <Link
              to="/register"
              onClick={() => trackHeroCtaClicked()}
              className={buttonClass("primary", "lg")}
            >
              {/* "Criar a conta da igreja" descrevia o formulário, não o que a
                  pessoa ganha ao clicar. O teste grátis é a promessa, e a
                  linha abaixo diz quando começa a cobrança. */}
              Testar grátis por 7 dias
            </Link>
            <a
              href="#precos"
              onClick={(e) => {
                e.preventDefault();
                trackHeroLearnMoreClicked();
                scrollToSection("precos");
              }}
              className={buttonClass("secondary", "lg")}
            >
              Ver planos e preços
            </a>
          </div>

          <p
            className="lp-enter mt-4 text-sm text-muted-foreground"
            style={{ "--lp-i": 2 } as React.CSSProperties}
          >
            7 dias grátis · cobrança só no 8º dia · implantação gratuita ·
            cancele quando quiser
          </p>

          {/* ── Prova social ──────────────────────────────────────────── */}
          <div
            className="lp-enter mt-10 flex flex-wrap items-center gap-x-6 gap-y-4"
            style={{ "--lp-i": 3 } as React.CSSProperties}
          >
            <div className="flex items-center gap-3">
              {/* Os rostos são reforço visual do número ao lado, que carrega a
                  informação — o leitor de tela não precisa ouvir oito nomes. */}
              <div className="flex -space-x-2" aria-hidden="true">
                {proofFaces.map((face) => (
                  <img
                    key={face.name}
                    src={face.src as string}
                    alt=""
                    title={face.name}
                    className="lp-img size-8 rounded-full object-cover ring-2 ring-background"
                    width={32}
                    height={32}
                  />
                ))}
              </div>
              {/* Volume em vez de contagem de clientes: "8 igrejas" lia como
                  risco para quem tem 1.500 membros. O total vive em três
                  lugares (aqui, a faixa do `trusted-by` e o AGENTS.md dos
                  e-mails): mexa nos três juntos. */}
              <p className="text-sm text-muted-foreground">
                <strong className="font-semibold text-foreground">
                  +4.600 pessoas
                </strong>{" "}
                geridas no Ecclesiato
              </p>
            </div>
            {/* Nota agregada. Some sozinha com menos de três avaliações
                aprovadas — ver reviews/use-reviews.ts. */}
            <RatingBadge />
          </div>
        </div>

        {/* ── Tela do produto ─────────────────────────────────────────── */}
        <div
          className="lp-enter relative mt-14 md:mt-16"
          style={{ "--lp-i": 4 } as React.CSSProperties}
        >
          <div className="lp-surface-lg overflow-hidden rounded-b-none">
            <HeroVideo />
          </div>
        </div>
      </div>

      {/* Os três ganhos ficam colados à tela, como legenda dela: é o que a
          pessoa acabou de ver o sistema fazer. */}
      <div className="relative border-t border-border bg-card">
        <ul
          className={`${CONTAINER} grid gap-3 py-5 text-sm sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-4`}
        >
          {proofPoints.map((point) => (
            <li key={point.who} className="flex gap-2.5">
              <Check
                className="mt-0.5 size-4 shrink-0 text-primary"
                strokeWidth={2}
                aria-hidden="true"
              />
              <span className="text-muted-foreground">
                <span className="font-semibold text-foreground">
                  {point.who}
                </span>{" "}
                {point.what}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
